// ── Display currency ──────────────────────────────────────────────────────────
// One rule for every price the Desk prints: render it in the currency the top-bar
// toggle has selected, converting when the observed currency differs. The point is
// comparison — a feed mixing "999 GEL" and "380 USD" cannot be scanned, and the
// agent's first question on every card is "is this in range?".
//
// Two things this deliberately does NOT do:
//
//   1. It never converts silently. A converted number is prefixed "≈" and carries
//      the observed price in its tooltip. The observation is what the ad says; the
//      conversion is ours. Presenting a derived number as an observed one is the
//      exact failure mode PHILOSOPHY.md exists to prevent.
//   2. It never invents a rate. We hold ONE rate (NBG daily USD→GEL). EUR listings
//      exist in the data and there is no EUR rate, so they render as observed
//      rather than through a guessed cross-rate. Same when the rate hasn't loaded.
//
// Rate direction: `rate` is GEL per 1 USD, matching getUsdGelRate()/NBG and the
// admin-set config.fx_to_gel used by the ranking code.
//
// Note this uses TODAY's rate for every card, not each listing's own fx_as_of
// snapshot. That is intentional here: the job is "compare these listings to each
// other right now", and a shared rate is the only way two cards are comparable.
// Historical restatement (what a price was worth on its listing date) is a
// different question and belongs to the price-history surfaces, not the card face.

export const CONVERTIBLE = new Set(['USD', 'GEL'])

// Returns null when the conversion cannot be done honestly — callers fall back to
// showing the observed price rather than a fabricated one.
export function convertMoney(amount, from, to, rate) {
  const a = Number(amount)
  if (!Number.isFinite(a)) return null
  if (!from || !to || from === to) return Number.isFinite(a) ? a : null
  if (!CONVERTIBLE.has(from) || !CONVERTIBLE.has(to)) return null
  if (!(Number(rate) > 0)) return null
  return from === 'USD' ? a * Number(rate) : a / Number(rate)
}

// → { text, approx, observed } | null
//   text     what to print
//   approx   true when `text` is a conversion (render the ≈ and the tooltip)
//   observed the observed price string, for the tooltip; null when not converted
//
// `suffix` carries the existing "/mo" · "/d" rent markers so callers don't have to
// re-append them around the currency.
// `counterpart` (2026-07-30) — the same amount in the OTHER convertible currency, for the
// hover tooltip. It is only set when the printed text is NOT a conversion: an `approx`
// price already carries the observed value in `observed`, and printing both would say the
// same thing twice. So every price on screen answers "and what is that in the other
// currency?" on hover, whichever direction it is being shown in.
// Still never invents a rate — convertMoney returns null for EUR or a missing rate, and
// then there is simply no counterpart rather than a guess.
function counterpartOf(n, cur, rate, suffix) {
  const other = cur === 'USD' ? 'GEL' : cur === 'GEL' ? 'USD' : null
  if (!other) return null
  const c = convertMoney(n, cur, other, rate)
  return c == null ? null : `≈${Math.round(c).toLocaleString('en-US')} ${other}` + suffix
}

export function formatMoney(amount, currency, display, rate, { suffix = '' } = {}) {
  if (amount == null || amount === '') return null
  const n = Number(amount)
  if (!Number.isFinite(n)) return null
  const cur = currency || display || ''
  const observed = `${n.toLocaleString('en-US')} ${cur}`.trim() + suffix

  if (!display || !cur || cur === display) {
    return { text: observed, approx: false, observed: null, counterpart: counterpartOf(n, cur, rate, suffix) }
  }

  const conv = convertMoney(n, cur, display, rate)
  if (conv == null) return { text: observed, approx: false, observed: null, counterpart: counterpartOf(n, cur, rate, suffix) }

  return {
    text: `≈${Math.round(conv).toLocaleString('en-US')} ${display}` + suffix,
    approx: true,
    observed,
    counterpart: null,
  }
}

// Plain-string convenience for the many one-line `${price} ${currency}` sites.
export function moneyText(amount, currency, display, rate, opts) {
  const m = formatMoney(amount, currency, display, rate, opts)
  return m ? m.text : null
}

// Comparable magnitude for sorting/deltas across mixed currencies. Falls back to
// the raw number when the pair isn't convertible, so a mixed list still orders
// sanely instead of collapsing to null.
export function comparableAmount(amount, currency, display, rate) {
  const c = convertMoney(amount, currency, display, rate)
  return c == null ? Number(amount) : c
}
